import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore,
  ExpandLess,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AutoAwesome as AutoIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  TrendingDown,
  Remove as FlatIcon,
  Info as InfoIcon,
  Psychology as AiIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { apiService } from '../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────────

function pnlColor(pct: number | null, theme: any): string {
  if (pct == null) return theme.palette.text.secondary;
  if (pct > 5)  return theme.palette.success.main;
  if (pct < -5) return theme.palette.error.main;
  return theme.palette.warning.main;
}

function PnlChip({ pct }: { pct: number | null }) {
  const theme = useTheme();
  if (pct == null) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const color = pnlColor(pct, theme);
  const icon = pct > 5 ? <TrendingUp fontSize="small" /> : pct < -5 ? <TrendingDown fontSize="small" /> : <FlatIcon fontSize="small" />;
  return (
    <Chip
      icon={icon}
      label={`${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`}
      size="small"
      sx={{ bgcolor: color + '20', color, fontWeight: 700, fontSize: '0.72rem' }}
    />
  );
}

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Enrichment badges ──────────────────────────────────────────────────────────

function EnrichmentBadges({ enrichment }: { enrichment: any }) {
  if (!enrichment) return null;
  const badges: Array<{ label: string; color: string; tooltip: string }> = [];

  if (enrichment.in_momentum_watch)
    badges.push({ label: 'Momentum Watch', color: '#2196f3', tooltip: 'Was in momentum watch list at trade time' });
  if (enrichment.in_emerging_watch)
    badges.push({ label: 'Emerging Watch', color: '#9c27b0', tooltip: 'Was in emerging breakout watch list' });

  const vscan = enrichment.volatility_scan;
  if (vscan?.recommendation === 'BUY' || vscan?.recommendation === 'STRONG_BUY')
    badges.push({ label: vscan.recommendation, color: '#4caf50', tooltip: `Volatility scanner score: ${vscan.score?.toFixed(0) ?? '?'}` });

  const news = enrichment.news_signals;
  if (news?.avg_direction != null) {
    const dir = news.avg_direction;
    const color = dir > 0.1 ? '#4caf50' : dir < -0.1 ? '#f44336' : '#ff9800';
    badges.push({ label: `News ${dir > 0 ? '+' : ''}${dir.toFixed(2)}`, color, tooltip: `${news.article_count} articles avg direction over 14d` });
  }

  const media = enrichment.media_trending;
  if (media?.total_mentions)
    badges.push({ label: `Media ${media.total_mentions}×`, color: '#ff9800', tooltip: `${media.total_mentions} mentions over ${media.scrape_days} days` });

  const stage = enrichment.market_stage;
  if (stage?.stage != null) {
    const stageColors: Record<number, string> = { 0: '#4caf50', 1: '#ff9800', 2: '#f44336', 3: '#e91e63', 4: '#9c27b0', 5: '#607d8b' };
    badges.push({ label: `Market Stage ${stage.stage}`, color: stageColors[stage.stage] ?? '#9e9e9e', tooltip: stage.stage_name ?? '' });
  }

  if (badges.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
      {badges.map(b => (
        <Tooltip key={b.label} title={b.tooltip} placement="top">
          <Chip label={b.label} size="small"
            sx={{ height: 18, fontSize: '0.6rem', bgcolor: b.color + '20', color: b.color, fontWeight: 700, cursor: 'default' }} />
        </Tooltip>
      ))}
    </Box>
  );
}

// ── Technicals snapshot ────────────────────────────────────────────────────────

function TechnicalsRow({ tech }: { tech: any }) {
  if (!tech) return null;
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
      {tech.off_52w_high_pct != null && (
        <Typography variant="caption" color="text.secondary">
          Off 52w-hi: <b>{tech.off_52w_high_pct}%</b>
        </Typography>
      )}
      {tech.ret_1m_pct != null && (
        <Typography variant="caption" color="text.secondary">
          1m: <b>{tech.ret_1m_pct > 0 ? '+' : ''}{tech.ret_1m_pct}%</b>
        </Typography>
      )}
      {tech.ret_3m_pct != null && (
        <Typography variant="caption" color="text.secondary">
          3m: <b>{tech.ret_3m_pct > 0 ? '+' : ''}{tech.ret_3m_pct}%</b>
        </Typography>
      )}
      {tech.above_ma50 != null && (
        <Typography variant="caption" color={tech.above_ma50 ? 'success.main' : 'error.main'}>
          {tech.above_ma50 ? '↑ MA50' : '↓ MA50'}
        </Typography>
      )}
      {tech.above_ma200 != null && (
        <Typography variant="caption" color={tech.above_ma200 ? 'success.main' : 'error.main'}>
          {tech.above_ma200 ? '↑ MA200' : '↓ MA200'}
        </Typography>
      )}
      {tech.vol_surge != null && (
        <Typography variant="caption" color="text.secondary">
          Vol: <b>{tech.vol_surge}x</b>
        </Typography>
      )}
    </Box>
  );
}

// ── Retrospective panel ────────────────────────────────────────────────────────

function RetrospectivePanel({ tradeId, onClose }: { tradeId: number; onClose: () => void }) {
  const theme = useTheme();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getTradeRetrospective(tradeId)
      .then(setData)
      .catch(e => setError(e?.response?.data?.detail ?? 'Failed to load retrospective'))
      .finally(() => setLoading(false));
  }, [tradeId]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trade Retrospective</DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress size={32} />}
        {error && <Alert severity="error">{error}</Alert>}
        {data && (
          <Box>
            {data.current_price && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>Current Price</Typography>
                <Typography variant="h5" fontWeight={800}>${data.current_price.toFixed(2)}</Typography>
                <Typography variant="caption" color="text.secondary">{fmtDate(data.current_date)}</Typography>
                {data.unrealized_pnl_pct != null && (
                  <Box sx={{ mt: 0.5 }}>
                    <PnlChip pct={data.unrealized_pnl_pct} />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      unrealized P&L
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {data.exit_signal && (
              <Alert severity={data.exit_signal.status === 'SELL' ? 'error' : 'warning'} sx={{ mb: 2 }}>
                Exit signal: <b>{data.exit_signal.status}</b> — {data.exit_signal.reason}
              </Alert>
            )}

            {data.signal_checks?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Signal Check</Typography>
                {data.signal_checks.map((c: any, i: number) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="caption">{c.signal}</Typography>
                    <Typography variant="caption" fontWeight={600}
                      color={c.verdict?.includes('BROKEN') || c.verdict?.includes('bearish') ? 'error.main' : 'text.primary'}>
                      {c.verdict}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Typography variant="caption" color="text.disabled">
              Generated {new Date(data.generated_at).toLocaleString()}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── AI recommendation panel ────────────────────────────────────────────────────

function AiRecommendationPanel({ trade, onClose, onRecommendationSaved }: { trade: any; onClose: () => void; onRecommendationSaved?: () => void }) {
  const theme = useTheme();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recSaved, setRecSaved] = useState(false);
  const [recSaving, setRecSaving] = useState(false);
  const isActive = trade.action_type === 'BUY' && !trade.exit_price;

  useEffect(() => {
    apiService.getTradeAiRecommendation(trade.id)
      .then(setData)
      .catch(e => setError(e?.response?.data?.detail ?? 'AI analysis failed'))
      .finally(() => setLoading(false));
  }, [trade.id]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AiIcon color="primary" />
        {isActive ? `AI Recommendation — ${trade.symbol}` : `Trade Retrospective — ${trade.symbol}`}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Gathering all signals and generating analysis… (this takes 15–30 seconds)
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {data && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Alert
                severity={isActive ? 'info' : 'success'}
                icon={<AiIcon />}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {data.analysis_type === 'recommendation'
                  ? 'Active position — HOLD / TRIM / SELL recommendation'
                  : 'Closed trade — retrospective analysis'}
              </Alert>
              {trade.position_type && (
                <Chip
                  label={`Strategy: ${STRATEGY_LABEL[trade.position_type] ?? trade.position_type}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                />
              )}
              {!trade.position_type && (
                <Chip
                  label="Strategy: unspecified"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 700, color: 'text.disabled', whiteSpace: 'nowrap' }}
                />
              )}
            </Box>

            {/* Main AI output */}
            <Box
              sx={{
                bgcolor: theme.palette.action.hover,
                borderRadius: 1,
                p: 2,
                mb: 2,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                lineHeight: 1.6,
              }}
            >
              {data.recommendation}
            </Box>

            {/* Key signals used */}
            {data.signals_used && (
              <Box>
                <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.5}>
                  KEY SIGNALS USED
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {data.signals_used.exit_signal && (
                    <Chip
                      label={`Exit: ${data.signals_used.exit_signal}`}
                      size="small"
                      color={data.signals_used.exit_signal === 'SELL' ? 'error' : data.signals_used.exit_signal === 'TRIM' ? 'warning' : 'success'}
                    />
                  )}
                  {data.signals_used.above_ma50 != null && (
                    <Chip
                      label={data.signals_used.above_ma50 ? '↑ MA50' : '↓ MA50'}
                      size="small"
                      color={data.signals_used.above_ma50 ? 'success' : 'error'}
                    />
                  )}
                  {data.signals_used.in_momentum_leaders && (
                    <Chip label="Momentum Leader" size="small" color="primary" />
                  )}
                  {data.signals_used.in_emerging_breakouts && (
                    <Chip label="Emerging Breakout" size="small" color="secondary" />
                  )}
                  {data.signals_used.market_stage != null && (
                    <Chip label={`Stage ${data.signals_used.market_stage}: ${data.signals_used.market_stage_name}`} size="small" />
                  )}
                  {data.signals_used.news_direction != null && (
                    <Chip
                      label={`News ${data.signals_used.news_direction > 0 ? '+' : ''}${data.signals_used.news_direction.toFixed(2)}`}
                      size="small"
                      color={data.signals_used.news_direction > 0.1 ? 'success' : data.signals_used.news_direction < -0.1 ? 'error' : 'default'}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Save recommendation for backtesting */}
            {isActive && data.recommendation && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                {recSaved || trade.recommendation_action ? (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    Recommendation saved: <b>{trade.recommendation_action || data.signals_used?.exit_signal || 'logged'}</b>
                    {trade.recommendation_date && ` on ${fmtDate(trade.recommendation_date)}`}
                    {' '}— will be used to evaluate outcome when you exit.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Log this recommendation for backtesting:
                    </Typography>
                    {(['HOLD', 'TRIM', 'SELL'] as const).map(action => (
                      <Button
                        key={action}
                        size="small"
                        variant="outlined"
                        disabled={recSaving}
                        color={action === 'SELL' ? 'error' : action === 'TRIM' ? 'warning' : 'success'}
                        onClick={async () => {
                          setRecSaving(true);
                          try {
                            await apiService.saveTradeRecommendation(trade.id, action, data.recommendation?.slice(0, 500));
                            setRecSaved(true);
                            onRecommendationSaved?.();
                          } catch { /* ignore */ } finally { setRecSaving(false); }
                        }}
                      >
                        {action}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2 }}>
              Generated {new Date(data.generated_at).toLocaleString()} · Not financial advice
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Trim dialog ────────────────────────────────────────────────────────────────

function TrimDialog({ trade, onSave, onClose }: { trade: any; onSave: () => void; onClose: () => void }) {
  const [sharesSold, setSharesSold] = useState('');
  const [exitPrice, setExitPrice]   = useState('');
  const [commission, setCommission] = useState('');
  const [trimDate, setTrimDate]     = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes]           = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const maxShares = trade.shares ?? null;
  const costBasis = trade.entry_price;
  const estimatedPnl = exitPrice && costBasis
    ? ((parseFloat(exitPrice) / costBasis - 1) * 100).toFixed(1)
    : null;
  const proceeds = sharesSold && exitPrice
    ? (parseFloat(sharesSold) * parseFloat(exitPrice)).toFixed(0)
    : null;
  // Net profit preview: gross − this order's commission − entry commission pro-rated by shares sold
  const netProfit = (() => {
    if (!sharesSold || !exitPrice || !costBasis) return null;
    const sh = parseFloat(sharesSold);
    const gross = (parseFloat(exitPrice) - costBasis) * sh;
    const exitComm = commission ? parseFloat(commission) : 0;
    const origShares = (trade.shares ?? 0) + (trade.trims ?? []).reduce((s: number, t: any) => s + (t.shares_sold ?? 0), 0);
    const entryCommProrated = trade.entry_commission && origShares > 0
      ? trade.entry_commission * (sh / origShares) : 0;
    return (gross - exitComm - entryCommProrated).toFixed(2);
  })();

  const handleSave = async () => {
    if (!sharesSold || !exitPrice || !trimDate) return;
    setSaving(true);
    try {
      await apiService.recordTrim(trade.id, parseFloat(sharesSold), parseFloat(exitPrice), trimDate,
        notes || undefined, commission ? parseFloat(commission) : undefined);
      onSave();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Trim Position — {trade.symbol}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {maxShares != null && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Current position: <b>{maxShares} shares</b> @ ${costBasis?.toFixed(2)} entry
          </Alert>
        )}
        <TextField
          label="Shares sold"
          type="number"
          value={sharesSold}
          onChange={e => setSharesSold(e.target.value)}
          helperText={maxShares != null ? `Max: ${maxShares}` : undefined}
          inputProps={{ min: 0.01, max: maxShares ?? undefined, step: 'any' }}
          fullWidth
        />
        <TextField
          label="Exit price"
          type="number"
          value={exitPrice}
          onChange={e => setExitPrice(e.target.value)}
          inputProps={{ min: 0.01, step: 'any' }}
          fullWidth
          helperText={estimatedPnl != null
            ? `Est. P&L: ${Number(estimatedPnl) >= 0 ? '+' : ''}${estimatedPnl}% · Proceeds: $${proceeds}`
            : undefined}
        />
        <TextField
          label="Commission (optional)"
          type="number"
          value={commission}
          onChange={e => setCommission(e.target.value)}
          inputProps={{ min: 0, step: 'any' }}
          fullWidth
          helperText={netProfit != null
            ? `Net profit (after commissions): ${Number(netProfit) >= 0 ? '+' : ''}$${netProfit}`
            : 'Broker fee on this sell order — from your broker\'s trade details'}
        />
        <TextField
          label="Trim date"
          type="date"
          value={trimDate}
          onChange={e => setTrimDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Notes (optional)"
          multiline
          rows={2}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          fullWidth
          placeholder="e.g. AI recommended TRIM 30% — hit target"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={saving || !sharesSold || !exitPrice || !trimDate}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Record Trim'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Exit dialog ────────────────────────────────────────────────────────────────

function ExitDialog({ tradeId, onSave, onClose }: { tradeId: number; onSave: () => void; onClose: () => void }) {
  const [price, setPrice] = useState('');
  const [commission, setCommission] = useState('');
  const [exitDate, setExitDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!price || !exitDate) return;
    setSaving(true);
    try {
      await apiService.recordExit(tradeId, parseFloat(price), exitDate,
        commission ? parseFloat(commission) : undefined);
      onSave();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Record Exit</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField label="Exit Price" type="number" fullWidth value={price}
          onChange={e => setPrice(e.target.value)} sx={{ mb: 2, mt: 1 }} inputProps={{ step: '0.01' }} />
        <TextField label="Commission (optional)" type="number" fullWidth value={commission}
          onChange={e => setCommission(e.target.value)} sx={{ mb: 2 }} inputProps={{ step: 'any', min: 0 }} />
        <TextField label="Exit Date" type="date" fullWidth value={exitDate}
          onChange={e => setExitDate(e.target.value)} InputLabelProps={{ shrink: true }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !price}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Edit trade dialog ─────────────────────────────────────────────────────────

function EditTradeDialog({ trade, onSave, onClose }: { trade: any; onSave: () => void; onClose: () => void }) {
  const [entryPrice, setEntryPrice] = useState(String(trade.entry_price ?? ''));
  const [tradeDate, setTradeDate]   = useState(trade.trade_date ?? '');
  const [shares, setShares]         = useState(String(trade.shares ?? ''));
  const [entryCommission, setEntryCommission] = useState(String(trade.entry_commission ?? ''));
  const [positionType, setPositionType] = useState(trade.position_type ?? '');
  const [notes, setNotes]           = useState(trade.notes ?? '');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const payload: any = {};
      if (entryPrice) payload.entry_price = parseFloat(entryPrice);
      if (tradeDate)  payload.trade_date  = tradeDate;
      payload.shares        = shares ? parseFloat(shares) : null;
      payload.position_type = positionType || null;
      payload.notes         = notes || null;
      payload.entry_commission = entryCommission ? parseFloat(entryCommission) : null;
      await apiService.updateTrade(trade.id, payload);
      onSave();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Trade — {trade.symbol}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField label="Entry Price" type="number" fullWidth size="small"
              value={entryPrice} onChange={e => setEntryPrice(e.target.value)}
              inputProps={{ step: '0.01' }} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Trade Date" type="date" fullWidth size="small"
              value={tradeDate} onChange={e => setTradeDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="# Shares" type="number" fullWidth size="small"
              value={shares} onChange={e => setShares(e.target.value)} placeholder="0" />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Entry Commission" type="number" fullWidth size="small"
              value={entryCommission} onChange={e => setEntryCommission(e.target.value)}
              placeholder="0.00" inputProps={{ step: 'any', min: 0 }} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Strategy" select fullWidth size="small"
              value={positionType} onChange={e => setPositionType(e.target.value)}>
              <MenuItem value="">Unspecified</MenuItem>
              <MenuItem value="long_term">📈 Long-term hold</MenuItem>
              <MenuItem value="quick_profit">⚡ Quick profit</MenuItem>
              <MenuItem value="momentum">🚀 Momentum follow</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notes" fullWidth size="small" multiline rows={2}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Entry thesis, context, reminders…" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Auto-strategy panel ───────────────────────────────────────────────────────

const STRATEGY_LABEL: Record<string, string> = {
  long_term:    '📈 Long-term hold',
  quick_profit: '⚡ Quick profit',
  momentum:     '🚀 Momentum follow',
};

function AutoStrategyPanel({ trade, onSaved, onClose }: { trade: any; onSaved: () => void; onClose: () => void }) {
  const [result, setResult]   = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    apiService.autoStrategy(trade.id)
      .then(r => { setResult(r); setLoading(false); })
      .catch(e => { setError(e?.response?.data?.detail ?? 'Analysis failed'); setLoading(false); });
  }, [trade.id]);

  const handleSave = async () => {
    if (!result?.recommended_strategy) return;
    setSaving(true);
    try {
      await apiService.updateTrade(trade.id, { position_type: result.recommended_strategy });
      setSaved(true);
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const hasExistingStrategy = !!trade.position_type;
  const isClassified = result?.recommended_strategy && result.recommended_strategy !== 'unclassified';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-detect Strategy — {trade.symbol}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Analysing technology, business model, and recent news…
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {result && (
          <Box sx={{ pt: 1 }}>
            {hasExistingStrategy && !saved && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Current strategy: <b>{STRATEGY_LABEL[trade.position_type] ?? trade.position_type}</b>
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" color="text.disabled" fontWeight={700}>RECOMMENDED</Typography>
              <Chip
                label={STRATEGY_LABEL[result.recommended_strategy] ?? result.recommended_strategy ?? 'Unknown'}
                color="primary" size="small"
              />
              {result.confidence && (
                <Chip label={`${result.confidence} confidence`} size="small" variant="outlined" />
              )}
            </Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>{result.reason}</Typography>
            {saved && <Alert severity="success">Strategy saved — AI recommendation will now use this frame.</Alert>}
            {!isClassified && !saved && (
              <Alert severity="warning">Could not confidently classify. You can still set strategy manually via Edit.</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{saved ? 'Done' : 'Cancel'}</Button>
        {isClassified && !saved && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : hasExistingStrategy ? 'Update strategy' : 'Save strategy'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Remaining position P&L (fetches current price from retrospective) ─────────

function RemainingPositionPnl({ trade }: { trade: any }) {
  const [retro, setRetro] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.getTradeRetrospective(trade.id)
      .then(setRetro)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [trade.id]);

  const remaining = trade.shares ?? 0;
  const entry = trade.entry_price ?? 0;
  const costBasis = remaining * entry;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">Loading current price…</Typography>
      </Box>
    );
  }

  const currentPrice = retro?.current_price;
  const unrealizedPct = currentPrice != null
    ? parseFloat(((currentPrice / entry - 1) * 100).toFixed(1))
    : null;
  const unrealizedDollar = currentPrice != null
    ? (currentPrice - entry) * remaining
    : null;

  return (
    <Box sx={{ mb: 1, p: 1, bgcolor: 'action.selected', borderRadius: 1 }}>
      <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.5}>
        REMAINING POSITION
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="caption">
          <strong>{remaining}</strong> shares @ ${entry.toFixed(2)} entry = <strong>${costBasis.toFixed(0)}</strong> cost
        </Typography>
        {currentPrice != null ? (
          <>
            <Typography variant="caption">
              Current: <strong>${currentPrice.toFixed(2)}</strong>
            </Typography>
            <Typography variant="caption">
              Current value: <strong>${(currentPrice * remaining).toFixed(0)}</strong>
            </Typography>
            {unrealizedPct != null && unrealizedDollar != null && (
              <Typography variant="caption" fontWeight={700}
                sx={{ color: unrealizedDollar >= 0 ? 'success.main' : 'error.main' }}>
                Unrealized: {unrealizedDollar >= 0 ? '+' : ''}${unrealizedDollar.toFixed(0)}
                {' '}({unrealizedPct >= 0 ? '+' : ''}{unrealizedPct}%)
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No recent price data available
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Trade row ──────────────────────────────────────────────────────────────────

function LifecycleBadge({ lc }: { lc: any }) {
  if (!lc) return null;
  const color = lc.action === 'SELL' ? 'error' : lc.action === 'TRIM' ? 'warning' : 'success';
  const holdText = lc.expected_hold && lc.expected_hold !== '—' ? ` · ${lc.expected_hold}` : '';
  const coreText = lc.is_core ? ' 🛡' : '';
  const tooltip = lc.is_core
    ? `${lc.reason} — Core position: ${lc.trim_pct}% already sold (${lc.original_shares} → ${lc.remaining_shares} shares, $${lc.realized_pnl >= 0 ? '+' : ''}${lc.realized_pnl} realized)`
    : lc.reason;
  return (
    <Tooltip title={tooltip} arrow placement="right">
      <Chip
        size="small"
        label={`${lc.action}${holdText}${coreText}`}
        color={color}
        variant={lc.action === 'HOLD' ? 'outlined' : 'filled'}
        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, mt: 0.25, cursor: 'default' }}
      />
    </Tooltip>
  );
}

function TradeRow({ trade, lifecycle, onRefresh }: { trade: any; lifecycle?: any; onRefresh: () => void }) {
  const theme = useTheme();
  const [expanded, setExpanded]     = useState(false);
  const [showRetro, setShowRetro]   = useState(false);
  const [showAi, setShowAi]         = useState(false);
  const [showExit, setShowExit]     = useState(false);
  const [showTrim, setShowTrim]     = useState(false);
  const [showEdit, setShowEdit]     = useState(false);
  const [showAutoStrat, setShowAutoStrat] = useState(false);
  const [deleting, setDeleting]     = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${trade.action_type} ${trade.symbol}?`)) return;
    setDeleting(true);
    try { await apiService.deleteTrade(trade.id); onRefresh(); }
    catch (e) { setDeleting(false); }
  };

  const isOpen = trade.action_type === 'BUY' && !trade.exit_price;
  const actionColor = trade.action_type === 'BUY' ? theme.palette.success.main : theme.palette.error.main;

  return (
    <>
      <TableRow
        hover
        sx={{ '& > *': { borderBottom: expanded ? 'none' : undefined } }}
      >
        <TableCell>
          <Chip label={trade.action_type} size="small"
            sx={{ bgcolor: actionColor + '20', color: actionColor, fontWeight: 700, fontSize: '0.7rem' }} />
        </TableCell>
        <TableCell>
          <Typography fontWeight={700}>{trade.symbol}</Typography>
          {trade.position_type && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {STRATEGY_LABEL[trade.position_type] ?? trade.position_type}
            </Typography>
          )}
          {isOpen && <LifecycleBadge lc={lifecycle} />}
        </TableCell>
        <TableCell>{fmtDate(trade.trade_date)}</TableCell>
        <TableCell>
          {(() => {
            const sold = (trade.trims ?? []).reduce((s: number, t: any) => s + (t.shares_sold ?? 0), 0);
            if (trade.shares == null && sold === 0)
              return <Typography variant="caption" color="text.disabled">—</Typography>;
            const remaining = trade.shares ?? 0;
            const original = remaining + sold;
            if (sold > 0) {
              return (
                <>
                  <Typography variant="body2" fontWeight={600}>{original} bought</Typography>
                  <Typography variant="caption" display="block" sx={{ color: '#F59E0B' }}>
                    {sold} sold
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {remaining} held
                  </Typography>
                </>
              );
            }
            return <Typography variant="body2" fontWeight={600}>{remaining}</Typography>;
          })()}
        </TableCell>
        <TableCell>${trade.entry_price?.toFixed(2)}</TableCell>
        <TableCell>
          {isOpen && lifecycle?.current_price != null ? (
            <>
              <Typography variant="body2" fontWeight={600}>${lifecycle.current_price.toFixed(2)}</Typography>
              {lifecycle.pnl_pct != null && (
                <Typography variant="caption" display="block" fontWeight={700}
                  sx={{ color: lifecycle.pnl_pct >= 0 ? 'success.main' : 'error.main' }}>
                  {lifecycle.pnl_pct >= 0 ? '+' : ''}{lifecycle.pnl_pct}%
                </Typography>
              )}
              {lifecycle.price_date && (
                <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                  {fmtDate(lifecycle.price_date)}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>
        <TableCell>
          {trade.exit_price ? `$${trade.exit_price.toFixed(2)}` : <Typography variant="caption" color="text.disabled">open</Typography>}
        </TableCell>
        <TableCell>
          {trade.pnl_pct != null ? (
            <PnlChip pct={trade.pnl_pct} />
          ) : (() => {
            const realized = (trade.trims ?? []).reduce((s: number, t: any) =>
              s + (t.net_pnl ?? ((t.exit_price != null && trade.entry_price != null)
                ? (t.exit_price - trade.entry_price) * (t.shares_sold ?? 0) : 0)), 0);
            return realized !== 0 ? (
              <Typography variant="caption" fontWeight={700}
                sx={{ color: realized >= 0 ? 'success.main' : 'error.main' }}>
                {realized >= 0 ? '+' : ''}${realized.toFixed(2)} realized
              </Typography>
            ) : <PnlChip pct={null} />;
          })()}
          {trade.benchmark && (
            <Tooltip title={trade.benchmark.after_tax_ils != null
              ? `In shekels: cost at ${trade.benchmark.usd_ils_entry?.toFixed(3)} ₪/$, value at ${trade.benchmark.usd_ils_exit?.toFixed(3)} (currency ${trade.benchmark.currency_effect_ils >= 0 ? '+' : ''}₪${Math.round(trade.benchmark.currency_effect_ils)}). Tax: 25% of the dollar gain, a rough guide — currency and inflation rules, losses in the same year and surtax change the real figure.`
              : 'SPY held over the same days, dividends included'}>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.62rem', mt: 0.3 }}>
                {trade.benchmark.spy_pct != null && <>SPY {trade.benchmark.spy_pct >= 0 ? '+' : ''}{trade.benchmark.spy_pct.toFixed(1)}% · <b style={{ color: trade.benchmark.vs_spy_pts >= 0 ? '#22C55E' : '#EF4444' }}>{trade.benchmark.vs_spy_pts >= 0 ? '+' : ''}{trade.benchmark.vs_spy_pts.toFixed(1)} pts</b></>}
                {trade.benchmark.after_tax_ils != null && <> · after tax ≈ ₪{Math.round(trade.benchmark.after_tax_ils).toLocaleString()}{trade.benchmark.open ? ' if sold today' : ''}</>}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {isOpen && (
              <>
                <Button size="small" variant="outlined" color="warning"
                  onClick={() => setShowTrim(true)} sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}>
                  Trim
                </Button>
                <Button size="small" variant="outlined" color="error"
                  onClick={() => setShowExit(true)} sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}>
                  Exit
                </Button>
              </>
            )}
            <Tooltip title={isOpen ? "AI recommendation (HOLD/TRIM/SELL)" : "AI retrospective analysis"}>
              <IconButton size="small" onClick={() => setShowAi(true)} color="primary">
                <AiIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={trade.position_type ? "Re-analyse strategy" : "Auto-detect strategy from news & technology"}>
              <IconButton size="small" onClick={() => setShowAutoStrat(true)} color="secondary">
                <AutoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit trade">
              <IconButton size="small" onClick={() => setShowEdit(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Data retrospective">
              <IconButton size="small" onClick={() => setShowRetro(true)}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={handleDelete} disabled={deleting} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expanded enrichment row */}
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, bgcolor: theme.palette.action.hover }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ py: 1.5, px: 2 }}>
              {trade.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  📝 {trade.notes}
                </Typography>
              )}

              {/* Lifecycle advice — rule-based, always visible for open positions */}
              {isOpen && lifecycle && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={lifecycle.action}
                    color={lifecycle.action === 'SELL' ? 'error' : lifecycle.action === 'TRIM' ? 'warning' : 'success'}
                    variant={lifecycle.action === 'HOLD' ? 'outlined' : 'filled'}
                    sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ pt: '2px' }}>
                    {lifecycle.reason}
                    {lifecycle.days_held != null && ` (${lifecycle.days_held}d held)`}
                    {lifecycle.is_core && lifecycle.original_shares > 0 &&
                      ` — parent order: ${lifecycle.original_shares} shares, ${lifecycle.trim_pct}% sold, ${lifecycle.remaining_shares} remaining`}
                  </Typography>
                </Box>
              )}

              {/* AI recommendation badge */}
              {trade.recommendation_action && (
                <Box sx={{ mb: 1 }}>
                  <Chip
                    size="small"
                    label={`AI rec: ${trade.recommendation_action} on ${fmtDate(trade.recommendation_date)}`}
                    color={trade.recommendation_action === 'SELL' ? 'error' : trade.recommendation_action === 'TRIM' ? 'warning' : 'success'}
                    sx={{ fontSize: '0.65rem', height: 20 }}
                  />
                </Box>
              )}

              {/* Trim history + realized/unrealized P&L summary */}
              {trade.trims?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.5}>
                    SOLD (child orders)
                  </Typography>
                  {trade.trims.map((t: any) => {
                    // Prefer backend net P&L (commission-adjusted); fall back to gross
                    const grossPnl = t.shares_sold != null && t.exit_price != null && trade.entry_price != null
                      ? (t.exit_price - trade.entry_price) * t.shares_sold
                      : null;
                    const netPnl = t.net_pnl ?? grossPnl;
                    return (
                      <Box key={t.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" fontWeight={600}>
                          Sold {t.shares_sold} @ ${t.exit_price?.toFixed(2)}
                        </Typography>
                        <PnlChip pct={t.pnl_pct} />
                        {netPnl != null && (
                          <Typography variant="caption" fontWeight={700}
                            sx={{ color: netPnl >= 0 ? 'success.main' : 'error.main' }}>
                            {netPnl >= 0 ? '+' : ''}${netPnl.toFixed(2)} net
                          </Typography>
                        )}
                        {t.commission != null && (
                          <Typography variant="caption" color="text.disabled">
                            (comm. ${t.commission.toFixed(2)})
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">{fmtDate(t.trim_date)}</Typography>
                      </Box>
                    );
                  })}
                  {/* Realized total — net of all commissions */}
                  {(() => {
                    const totalRealized = trade.trims.reduce((sum: number, t: any) => {
                      if (t.net_pnl != null) return sum + t.net_pnl;
                      if (t.shares_sold != null && t.exit_price != null && trade.entry_price != null)
                        return sum + (t.exit_price - trade.entry_price) * t.shares_sold;
                      return sum;
                    }, 0);
                    const totalSharesTrimmed = trade.trims.reduce((s: number, t: any) => s + (t.shares_sold ?? 0), 0);
                    const hasCommissions = trade.trims.some((t: any) => t.commission != null) || trade.entry_commission != null;
                    return (
                      <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          Realized profit from {totalSharesTrimmed} shares sold:{' '}
                          <strong style={{ color: totalRealized >= 0 ? '#4caf50' : '#f44336' }}>
                            {totalRealized >= 0 ? '+' : ''}${totalRealized.toFixed(2)}
                          </strong>
                          {hasCommissions && ' (net of commissions)'}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {/* Remaining position unrealized P&L */}
              {trade.action_type === 'BUY' && !trade.exit_price && trade.shares != null && (
                <RemainingPositionPnl trade={trade} />
              )}

              <Typography variant="caption" color="text.disabled" fontWeight={700} display="block" mb={0.5}>
                SIGNALS AT ENTRY ({fmtDate(trade.trade_date)})
              </Typography>
              <EnrichmentBadges enrichment={trade.enrichment} />
              <TechnicalsRow tech={trade.enrichment?.technicals} />
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                {trade.shares && (
                  <Chip size="small" label={`${trade.shares} shares remaining · $${(trade.shares * trade.entry_price).toFixed(0)} cost`}
                    sx={{ fontSize: '0.65rem', height: 18 }} />
                )}
                {trade.position_type && (
                  <Chip size="small"
                    label={({'long_term': '📈 Long-term', 'quick_profit': '⚡ Quick profit', 'momentum': '🚀 Momentum'} as Record<string,string>)[trade.position_type] ?? trade.position_type}
                    sx={{ fontSize: '0.65rem', height: 18 }} />
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {showAi && <AiRecommendationPanel trade={trade} onClose={() => setShowAi(false)} onRecommendationSaved={onRefresh} />}
      {showRetro && <RetrospectivePanel tradeId={trade.id} onClose={() => setShowRetro(false)} />}
      {showExit && <ExitDialog tradeId={trade.id} onSave={() => { setShowExit(false); onRefresh(); }} onClose={() => setShowExit(false)} />}
      {showTrim && <TrimDialog trade={trade} onSave={() => { setShowTrim(false); onRefresh(); }} onClose={() => setShowTrim(false)} />}
      {showEdit && <EditTradeDialog trade={trade} onSave={() => { setShowEdit(false); onRefresh(); }} onClose={() => setShowEdit(false)} />}
      {showAutoStrat && <AutoStrategyPanel trade={trade} onSaved={onRefresh} onClose={() => setShowAutoStrat(false)} />}
    </>
  );
}

// ── Log trade form ─────────────────────────────────────────────────────────────

function LogTradeForm({ onSaved }: { onSaved: () => void }) {
  const [symbol, setSymbol]           = useState('');
  const [actionType, setActionType]   = useState('BUY');
  const [tradeDate, setTradeDate]     = useState(new Date().toISOString().split('T')[0]);
  const [entryPrice, setEntryPrice]   = useState('');
  const [shares, setShares]           = useState('');
  const [positionType, setPositionType] = useState('');
  const [notes, setNotes]             = useState('');
  const [exitPrice, setExitPrice]     = useState('');
  const [exitDate, setExitDate]       = useState('');
  const [entryCommission, setEntryCommission] = useState('');
  const [exitCommission, setExitCommission]   = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState(false);

  const handleSubmit = async () => {
    if (!symbol || !entryPrice || !tradeDate) return;
    setSaving(true); setError(null); setSuccess(false);
    try {
      await apiService.logTrade({
        symbol: symbol.trim().toUpperCase(),
        action_type: actionType,
        trade_date: tradeDate,
        entry_price: parseFloat(entryPrice),
        shares: shares ? parseFloat(shares) : undefined,
        position_type: positionType || undefined,
        notes: notes || undefined,
        exit_price: exitPrice ? parseFloat(exitPrice) : undefined,
        exit_date: exitDate || undefined,
        entry_commission: entryCommission ? parseFloat(entryCommission) : undefined,
        exit_commission: exitCommission ? parseFloat(exitCommission) : undefined,
      });
      setSuccess(true);
      setSymbol(''); setEntryPrice(''); setShares(''); setPositionType(''); setNotes(''); setExitPrice(''); setExitDate('');
      setEntryCommission(''); setExitCommission('');
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to save trade');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          <AddIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Log a Trade
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Entry signals are captured automatically from the app's current data.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Trade logged and enriched with current signals.</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField label="Symbol" value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())}
              fullWidth size="small" placeholder="NTRA" />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Action" select value={actionType} onChange={e => setActionType(e.target.value)} fullWidth size="small">
              <MenuItem value="BUY">BUY</MenuItem>
              <MenuItem value="SELL">SELL</MenuItem>
              <MenuItem value="BUY_SELL">BUY & SELL</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Date" type="date" value={tradeDate} onChange={e => setTradeDate(e.target.value)}
              fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Entry Price" type="number" value={entryPrice} onChange={e => setEntryPrice(e.target.value)}
              fullWidth size="small" inputProps={{ step: '0.01' }} />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField label="# Shares" type="number" value={shares} onChange={e => setShares(e.target.value)}
              fullWidth size="small" placeholder="0" />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField label="Commission" type="number" value={entryCommission} onChange={e => setEntryCommission(e.target.value)}
              fullWidth size="small" placeholder="0.00" inputProps={{ step: 'any', min: 0 }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Strategy" select value={positionType} onChange={e => setPositionType(e.target.value)} fullWidth size="small">
              <MenuItem value="">Unspecified</MenuItem>
              <MenuItem value="long_term">Long-term hold</MenuItem>
              <MenuItem value="quick_profit">Quick profit</MenuItem>
              <MenuItem value="momentum">Momentum follow</MenuItem>
            </TextField>
          </Grid>

          {(actionType === 'SELL' || actionType === 'BUY_SELL') && (
            <>
              <Grid item xs={6} sm={2}>
                <TextField label="Exit Price" type="number" value={exitPrice} onChange={e => setExitPrice(e.target.value)}
                  fullWidth size="small" inputProps={{ step: '0.01' }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="Exit Date" type="date" value={exitDate} onChange={e => setExitDate(e.target.value)}
                  fullWidth size="small" InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="Exit Commission" type="number" value={exitCommission} onChange={e => setExitCommission(e.target.value)}
                  fullWidth size="small" placeholder="0.00" inputProps={{ step: 'any', min: 0 }} />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={actionType === 'BUY' ? 9 : 5}>
            <TextField label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)}
              fullWidth size="small" placeholder="Why did you make this trade?" />
          </Grid>

          <Grid item xs={12} sm={1.5}>
            <Button variant="contained" fullWidth onClick={handleSubmit}
              disabled={saving || !symbol || !entryPrice}
              sx={{ height: 40 }}>
              {saving ? <CircularProgress size={18} /> : 'Log'}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const TradeJournal: React.FC = () => {
  const [trades, setTrades] = useState<any[]>([]);
  const [lifecycles, setLifecycles] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [tradeData, lcData] = await Promise.allSettled([
        apiService.listTrades(),
        apiService.getTradeLifecycles(),
      ]);
      if (tradeData.status === 'fulfilled') setTrades(tradeData.value.trades ?? []);
      if (lcData.status === 'fulfilled') {
        const map: Record<number, any> = {};
        for (const lc of (lcData.value.lifecycles ?? [])) map[lc.trade_id] = lc;
        setLifecycles(map);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openTrades  = trades.filter(t => t.action_type === 'BUY' && !t.exit_price);
  const closedTrades = trades.filter(t => t.exit_price);
  const totalPnl = closedTrades.reduce((sum, t) => sum + (t.pnl_pct ?? 0), 0);
  const avgPnl = closedTrades.length ? totalPnl / closedTrades.length : 0;

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} gutterBottom>Trade Journal</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Log your trades — enriched with every signal the app had at entry time.
        Retrospective analysis shows what signals said vs. what actually happened.
      </Typography>

      {/* Stats row */}
      {trades.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: '12px !important', textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled">Total Trades</Typography>
                <Typography variant="h5" fontWeight={800}>{trades.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: '12px !important', textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled">Open Positions</Typography>
                <Typography variant="h5" fontWeight={800} color="primary.main">{openTrades.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: '12px !important', textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled">Closed Trades</Typography>
                <Typography variant="h5" fontWeight={800}>{closedTrades.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: '12px !important', textAlign: 'center' }}>
                <Typography variant="caption" color="text.disabled">Avg P&L (closed)</Typography>
                <Typography variant="h5" fontWeight={800}
                  color={avgPnl > 0 ? 'success.main' : avgPnl < 0 ? 'error.main' : 'text.primary'}>
                  {avgPnl > 0 ? '+' : ''}{avgPnl.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <LogTradeForm onSaved={load} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Trade History</Typography>
        <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
      </Box>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && trades.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No trades logged yet. Use the form above to log your first trade.</Typography>
        </Paper>
      )}

      {!loading && trades.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={90}>Action</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Shares</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell>Current</TableCell>
                <TableCell>Exit</TableCell>
                <TableCell>P&L</TableCell>
                <TableCell width={180}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades.map(t => (
                <TradeRow key={t.id} trade={t} lifecycle={lifecycles[t.id]} onRefresh={load} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TradeJournal;
